import { ref, computed, watch } from 'vue'
import { useCore } from '@/composables/core'
import { isBooksyCom, useLogger } from '@booksy-com/vue-tools'
import { useEmitter } from '@/composables/emitter'
import { useSurvicate } from '@/composables/survicate/index.js'
import { useStorage } from '@/composables/storage/index.js'
import { createSegment } from './segment.js'

import {
  useEppo,
  whenEppoIsReady,
  EppoExperiments,
} from '@/composables/eppo/index.js'

import {
  ENDPOINTS,
  EVENTS,
  WEBVIEW_TYPES,
  SESSION_STORAGE_KEYS,
} from '@/lib/consts.js'

import AnalyticsManager from '@/lib/analytics/manager.js'

import {
  DEVICE,
  EVENT_ACTIONS,
  GA4_MEASUREMENT_ID,
  SCREEN_NAMES,
} from '@/lib/analytics/consts.js'

import { countBy, eqBy, identity } from 'ramda'
import { useAnalyticsCommonParams } from './common-params.js'
import { experimentViewed } from './events/index.js'
import { useOnboardingStore } from '@/stores/onboarding/index.js'

export { TRACK_EVENTS, PAGE_EVENTS } from './consts.js'
export * from './events/index.js'

let categories = null
let dispatchProcessing = false
let dispatchQueue = []
let manager = null
let core = null
let eppoReady = false
let businessSwitchedForced = false
let segment = null
let commonParams = {}

const logger = useLogger('analytics')
const storageSession = useStorage('session')

const shouldSendEventsOnTestEnv = ref(false)

const setSendingEventsOnTestEnv = (isEnabled) => {
  shouldSendEventsOnTestEnv.value = Boolean(isEnabled)
  segment?.setEnabledOnTestEnv(isEnabled)

  isEnabled
    ? storageSession.setItem(storageSession.KEYS.SEND_ANALYTICS, isEnabled)
    : storageSession.removeItem(storageSession.KEYS.SEND_ANALYTICS)
}

const _checkIfEventsShouldBeSentOnTestEnv = () => {
  if (isBooksyCom()) return

  const queryParams = new URLSearchParams(window.location.search)
  const sendAnalyticsEnabled = queryParams.get('send_analytics') === '1'
    || storageSession.getItem(storageSession.KEYS.SEND_ANALYTICS)

  setSendingEventsOnTestEnv(sendAnalyticsEnabled)
}

const _loadFirebaseAppIdFromGTag = () => new Promise((resolve) => {
  const MAX_RETRIES = 10

  const tryToLoad = (retry = 1) => {
    if (typeof window.gtag === 'function') {
      window.gtag('get', GA4_MEASUREMENT_ID, 'client_id', (clientId) => {
        resolve(clientId)
      })
    } else if (retry < MAX_RETRIES) {
      setTimeout(() => {
        tryToLoad(++retry)
      }, 1000)
    } else {
      logger.error('gtag not available')
      resolve(null)
    }
  }

  tryToLoad()
})

const _loadFirebaseAppIdFromWebView = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get('firebase_app_id')
}

const _loadFirebaseAppId = async (webView) => {
  let firebaseAppId

  if ([WEBVIEW_TYPES.ANDROID, WEBVIEW_TYPES.IOS].includes(webView)) {
    firebaseAppId = _loadFirebaseAppIdFromWebView()
  } else {
    firebaseAppId = await _loadFirebaseAppIdFromGTag()
  }

  if (firebaseAppId) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.FIREBASE_APP_ID, firebaseAppId)
  }
}

const _getPrimaryCategory = (business, categories) => {
  if (!Array.isArray(categories)) {
    return null
  }

  const primaryCategory = categories.find(category =>
    category?.id === business.primary_category
  )

  return primaryCategory || null
}

const _checkIfCategoriesChanged = (ids) => {
  // force change if categories not set
  if (!categories) {
    return true
  }

  const categoriesIds = categories.map(category => category.id)
  const notChanged = eqBy(countBy(identity), ids, categoriesIds)

  return !notChanged
}

const _getBusinessCategories = async (business) => {
  business = business || {}
  const { apiCall } = core

  const ids = Array.isArray(business.categories) ? business.categories : []

  if (!ids.length) {
    return []
  }

  if (!_checkIfCategoriesChanged(ids)) {
    return categories
  }

  return apiCall({
    url: ENDPOINTS.BUSINESS_CATEGORIES,
    urlParams: {
      businessId: business.id,
    },
  }).then(({ data }) => {
    return Array.isArray(data?.categories) ? data.categories.filter(category => ids.includes(category.id)) : []
  }).catch(() => [])
}

const _dispatchAction = async (name, data) => {
  const {
    app,
    identity,
    routingConfig,
  } = core.store.state

  const {
    appType,
    country,
    fingerprint,
    ios_fingerprint,
    webView,
  } = app

  const onboardingStore = useOnboardingStore()
  const business = core.store.state.business.business || onboardingStore.business
  const user = identity.user || onboardingStore.user

  const device = DEVICE[webView] || DEVICE.www
  categories = await _getBusinessCategories(business)
  const primaryCategory = _getPrimaryCategory(business, categories)

  return manager.dispatch({
    name,
    data,
    appType,
    user,
    business,
    country,
    device,
    primaryCategory,
    categories,
    fingerprint,
    ios_fingerprint,
    routingConfig: routingConfig.config,
  })
}

/*
 * accepts args. in 2 formats:
 *  - dispatch(name, data)
 *  - dispatch([ name, data ])
 */
const dispatch = async (name, data) => {
  // alternative args. format
  if (Array.isArray(name)) {
    [name, data] = name
  }

  if (!Array.isArray(dispatchQueue)) {
    dispatchQueue = []
  }

  if (dispatchProcessing || !core || !eppoReady) {
    dispatchQueue.push({ name, data })
    return
  }

  dispatchProcessing = true

  try {
    await _dispatchAction(name, data)
  } catch (e) {
    logger.error(e)
  }

  useSurvicate().invokeAnalyticsEvent(name, data)
  dispatchProcessing = false

  if (dispatchQueue.length) {
    const event = dispatchQueue.shift()

    dispatch(event.name, event.data)
  }
}

const _watchBusinessIdChange = () => {
  const businessId = computed(() => core.store.state.business?.business?.id)

  watch(businessId,
    (newId, oldId) => {
      const notForced = !businessSwitchedForced
      const businessSwitched = newId !== oldId
      const canBeSwitched = notForced || businessSwitched

      if (canBeSwitched) {
        dispatch(EVENTS.BUSINESS_SWITCHED)
        businessSwitchedForced = true
      }
    },
    { immediate: true }
  )
}

export function createAnalytics () {
  // additional module variable reset for testing purposes
  eppoReady = false

  core = useCore()
  segment = createSegment()

  commonParams = useAnalyticsCommonParams()

  const { webView } = core.store.state.app

  const emitter = useEmitter()
  emitter.on(emitter.EVENTS.ANALYTICS_DISPATCH, dispatch)

  manager = new AnalyticsManager({
    logger,
    webView,
  })

  _loadFirebaseAppId(webView)
  _watchBusinessIdChange()
  _checkIfEventsShouldBeSentOnTestEnv()

  whenEppoIsReady.then(() => {
    try {
      eppoReady = true
      const eppo = useEppo()

      // register eppo assignment callback
      eppo.on((assignment) => {
        const isExperiment = Object.keys(EppoExperiments)
          .some(name => assignment?.experiment?.includes(name))

        if (!isExperiment) return

        const data = {
          experimentName: assignment.experiment,
          subject: assignment.subject,
          subjectType: assignment.subjectAttributes.subject_type,
          variation: assignment.variation,
          assignmentTimestamp: assignment.timestamp,
          appDomain: assignment.subjectAttributes.app_domain,
        }

        experimentViewed(assignment)

        dispatch(
          EVENTS.EXPERIMENT_ACTION,
          {
            screenName: SCREEN_NAMES.EXPERIMENT_ASSIGNMENT,
            eventAction: EVENT_ACTIONS.VARIATION_ASSIGNED,
            ...data,
          }
        )
      })
    } catch (e) {
      logger.error(e)
    }
  })
}

export function useAnalytics () {
  const shouldSendEventsExternally = computed(() =>
    isBooksyCom() || shouldSendEventsOnTestEnv.value
  )

  return {
    commonParams,
    dispatch,
    EVENT_ACTIONS,
    EVENTS,
    manager,
    shouldSendEventsExternally,
    setSendingEventsOnTestEnv,

    page: (...args) => segment?.page(...args),
    track: (...args) => segment?.track(...args),
    identify: (...args) => segment?.identify(...args),
    reset: (...args) => segment?.reset(...args),
  }
}
